/**
 * The analytics service.
 *
 * `track(type, params)` is the single path every `/dye` event takes:
 * bot short-circuit → common params → caller params → attribution ids →
 * tracker fanout. The kernel knows zero event-specific fields; each event's
 * owner builds its own params and calls `track()` directly.
 */

import { isBotTraffic } from '../ua/bot';
import { warn } from '../../util/log';
import { getSdkStateOrNull } from '../state';
import { buildCommonParams } from './commonParams';
import { mergeAttributionIdsInto } from './attributionIds';
import { dispatchExpectingResponse, dispatchToTrackers } from './transport';

export type EventParamValue = string | number | boolean | null | undefined;
export type EventParams = Record<string, EventParamValue>;

export function track(type: string, params: EventParams = {}): void {
  trackToRoute('/dye', type, params);
}

/**
 * Report a classified bot. Bypasses the `isBotTraffic()` short-circuit that
 * every other `track()` call hits — matching yoto-web, which fires `isbt`
 * before the analytics-core bot flag is armed.
 */
export function trackBotDetected(info: string): void {
  const state = getSdkStateOrNull();
  if (!state) {
    warn(`trackBotDetected('${info}') called before init resolved — dropped`);
    return;
  }
  const args = buildCommonParams(state);
  mergeAttributionIdsInto(args, { persistCookies: state.engine.getStatus() === 'granted' });
  args.type = 'isbt';
  args.info = info;
  dispatchToTrackers(state.config.analytics.trackers, args);
}

/**
 * Prediction event — the `/p` route IS the event, so no `type=` param.
 * The consumer's PredictionRoute writes the covered impression + revenue
 * from the payload (`bidder`, `bid`, `group`, ...).
 */
export function trackPrediction(params: EventParams = {}): void {
  trackToRoute('/p', null, params);
}

/**
 * Prediction event whose response is read back — the consumer returns the price
 * it recorded, which feeds the session value. Resolves `null` when the event was
 * dropped or the response was unusable; callers fall back to the bid they sent.
 */
export function trackPredictionForValue(params: EventParams = {}): Promise<string | null> {
  const args = buildEventArgs('/p', null, params);
  if (!args) return Promise.resolve(null);
  return dispatchExpectingResponse(args.trackers, args.params, '/p');
}

function buildEventArgs(
  route: string,
  type: string | null,
  params: EventParams,
): { trackers: string[]; params: EventParams } | null {
  const state = getSdkStateOrNull();
  if (!state) {
    warn(`track('${type ?? route}') called before init resolved — dropped`);
    return null;
  }
  if (isBotTraffic()) return null;

  const args = buildCommonParams(state);
  for (const [key, value] of Object.entries(params)) {
    if (key === 'type' || value === null || value === undefined) continue;
    args[key] = value;
  }
  mergeAttributionIdsInto(args, { persistCookies: state.engine.getStatus() === 'granted' });
  if (type !== null) args.type = type;

  return { trackers: state.config.analytics.trackers, params: args };
}

function trackToRoute(route: string, type: string | null, params: EventParams = {}): void {
  const args = buildEventArgs(route, type, params);
  if (!args) return;
  dispatchToTrackers(args.trackers, args.params, route);
}
